import { Fingerprint, Mail } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { useFormWithValidation } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { LoginDto } from '@splitfiesta/shared';
import { Alert, Card, FormButton, FormField, H1, Text } from '@splitfiesta/ui';

import { LegalText } from '@/components/auth/LegalText';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import { AuthPageSkeleton } from '@/components/common/AuthPageSkeleton';
import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { StoreButtons } from '@/components/common/StoreButtons';
import { Footer } from '@/components/layout/Footer';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { hasStoredPasskeyEmail } from '@/hooks/queries/usePasskeys';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import { useAuthStore } from '@/stores/authStore';

export function LoginPage() {
  const [formData, setFormData] = useState<LoginDto>({
    email: '',
    password: '',
  });
  const [isLoginSuccessful, setIsLoginSuccessful] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Check if user has a stored passkey email for usernameless sign-in
  const hasPasskeyStored = hasStoredPasskeyEmail();

  const user = useAuthStore(state => state.user);
  const {
    login,
    isLoggingIn,
    loginError,
    signInWithPasskey,
    isSigningInWithPasskey,
    passkeySignInError,
    isPasskeySupported,
  } = useAuth();
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const { t, ready } = useTranslation();
  const { handleError } = useErrorHandler('auth');

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');
  const errorsLoaded = useNamespace('errors');

  // Get the invitation token and language from URL params
  const inviteToken = searchParams.get('invite');
  const langParam = searchParams.get('lang');

  useEffect(() => {
    if (user) {
      // Clear any stored redirect path to prevent confusion
      sessionStorage.removeItem('redirectAfterLogin');

      // If there's an invitation token, redirect back to the invitation page
      if (inviteToken) {
        router.push(`/invite/${inviteToken}`);
      } else {
        router.push('/groups');
      }
    }
  }, [user, router, inviteToken]);

  // Form validation and submission
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    error,
    setError,
  } = useFormWithValidation<LoginDto>({
    onSubmit: async (data: LoginDto) => {
      login(data);
      // Mark login as successful to keep loading state
      setIsLoginSuccessful(true);
      // Keep loading state during redirect
      // The redirect is handled by the login function in useAuth hook
      // and the useEffect above
    },
    // No onError callback needed - React Query mutation errors are handled via loginError useEffect
    validateFn: (data: LoginDto) => {
      if (!data.email.trim()) {
        return t('auth:errors.emailRequired');
      }
      if (!data.password) {
        return t('auth:errors.passwordRequired');
      }
      return null;
    },
  });

  // Handle login errors from the mutation
  // This is the primary error handler for API errors
  useEffect(() => {
    if (loginError) {
      // Check if user needs to verify their email first
      const errorCode =
        (loginError as { code?: string }).code ||
        (loginError as { name?: string }).name;
      if (errorCode === 'UserNotConfirmedException' && formData.email) {
        router.push(
          `/verify-email?email=${encodeURIComponent(formData.email)}`
        );
        return;
      }

      // Use setTimeout to avoid setting state during render
      const timer = setTimeout(() => {
        setError(handleError(loginError));
        setIsLoginSuccessful(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [loginError, handleError, setError, formData.email, router]);

  // Handle passkey login errors
  useEffect(() => {
    if (passkeySignInError) {
      const timer = setTimeout(() => {
        setError(handleError(passkeySignInError));
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [passkeySignInError, handleError, setError]);

  const handlePasskeySignIn = () => {
    setError('');
    // Email is optional - discoverable credentials allow sign-in without username
    signInWithPasskey(formData.email.trim() || undefined);
  };

  // Combined loading state: either submitting or successfully logged in or React Query is pending
  const isLoading =
    isSubmitting || isLoginSuccessful || isLoggingIn || isSigningInWithPasskey;

  // Show skeleton loader while translations are loading
  if (!ready || !authLoaded || !commonLoaded || !errorsLoaded) {
    return <AuthPageSkeleton fieldCount={2} />;
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 pt-4">
        <div className="max-w-md w-full mx-auto">
          {/* Theme Toggle and Language Switcher - Close to top */}
          <div className="flex justify-between items-center gap-3 mb-3">
            <ThemeToggle variant="auth" data-testid="auth-theme-toggle" />
            <LanguageSwitch variant="auth" />
          </div>

          {/* Logo - Second row */}
          <div className="text-center mb-8">
            <RouteChangeLink href="/" className="inline-block">
              <div className="relative inline-block">
                <H1 className="text-white hover:text-white/90 transition-colors cursor-pointer drop-shadow-lg mb-2">
                  {t('common:app.name')}
                </H1>
              </div>
            </RouteChangeLink>
            <Text className="text-white/90">{t('common:app.tagline')}</Text>
          </div>

          <StoreButtons className="mb-6 justify-center" />

          {/* Login Form */}
          <Card>
            <div className="text-center mb-6">
              <Text className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('auth:login.title')}
              </Text>
              <Text
                variant="bodySmall"
                className="mt-1 text-gray-500 dark:text-gray-400"
              >
                {t('auth:login.subtitle')}
              </Text>
            </div>

            {error && (
              <Alert
                type="error"
                message={String(error)}
                className="mb-6"
                data-testid="login-error-message"
              />
            )}

            {/* Social Login Buttons */}
            <SocialLoginButtons
              variant="login"
              disabled={isLoading}
              oauthState={{
                invite: inviteToken || undefined,
                lang: langParam || undefined,
              }}
            />

            {/* Social sign-in auto-creates an account for first-time users,
                so consent has to be captured here too — implicit notice, the
                accepted pattern for OAuth flows. */}
            <Text
              variant="bodySmall"
              className="mt-3 text-center text-slate-500 dark:text-slate-400"
              data-testid="login-terms-notice"
            >
              <LegalText text={t('auth:legal.termsNotice')} />
            </Text>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                  {t('auth:login.orContinueWith')}
                </span>
              </div>
            </div>

            {/* Passkey-only mode when user has stored passkey and hasn't clicked to show email form */}
            {hasPasskeyStored && isPasskeySupported && !showEmailForm ? (
              <div className="space-y-3">
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={handlePasskeySignIn}
                  loading={isSigningInWithPasskey}
                  loadingText={t('common:actions.loading')}
                  disabled={isLoading}
                  fullWidth
                  icon={Fingerprint}
                  data-testid="login-passkey-button"
                >
                  {t('auth:login.continueWithPasskey')}
                </FormButton>

                <button
                  type="button"
                  onClick={() => setShowEmailForm(true)}
                  className="w-full text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  data-testid="login-use-email-instead"
                >
                  {t('auth:login.useEmailInstead')}
                </button>
              </div>
            ) : !showEmailForm ? (
              /* "Continue with Email" button - matches social buttons styling */
              <FormButton
                type="button"
                variant="outline"
                onClick={() => setShowEmailForm(true)}
                disabled={isLoading}
                fullWidth
                icon={Mail}
                className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                data-testid="login-email-button"
              >
                {t('auth:login.continueWithEmail')}
              </FormButton>
            ) : (
              /* Email Form */
              <form
                onSubmit={e => handleFormSubmit(e, formData)}
                className="space-y-4"
                data-testid="login-form"
              >
                <FormField
                  id="email"
                  type="email"
                  label={t('common:forms.email')}
                  value={formData.email}
                  onChange={value =>
                    setFormData(prev => ({ ...prev, email: value }))
                  }
                  placeholder={t('auth:login.emailPlaceholder')}
                  required
                  autoFocus
                  disabled={isLoading}
                  autoComplete="off"
                  data-testid="login-email-input"
                />

                <FormField
                  id="password"
                  type="password"
                  label={t('common:forms.password')}
                  value={formData.password}
                  onChange={value =>
                    setFormData(prev => ({ ...prev, password: value }))
                  }
                  placeholder={t('auth:login.passwordPlaceholder')}
                  required
                  disabled={isLoading}
                  autoComplete="off"
                  data-testid="login-password-input"
                  showPasswordLabel={t('common:forms.showPassword')}
                  hidePasswordLabel={t('common:forms.hidePassword')}
                />

                <div className="text-right">
                  <RouteChangeLink
                    href="/forgot-password"
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    data-testid="login-forgot-password-link"
                  >
                    {t('auth:forgotPassword.title')}
                  </RouteChangeLink>
                </div>

                {/* Passkey Sign In Button - Primary (preferred/faster path) */}
                {isPasskeySupported && (
                  <FormButton
                    type="button"
                    variant="primary"
                    onClick={handlePasskeySignIn}
                    loading={isSigningInWithPasskey}
                    loadingText={t('common:actions.loading')}
                    disabled={isLoading}
                    fullWidth
                    icon={Fingerprint}
                    data-testid="login-passkey-button"
                  >
                    {t('auth:login.continueWithPasskey')}
                  </FormButton>
                )}

                {/* Password Sign In - Secondary (fallback) */}
                <FormButton
                  type="submit"
                  variant="outline"
                  loading={isLoggingIn || isLoginSuccessful}
                  loadingText={t('common:actions.loading')}
                  disabled={isLoading}
                  fullWidth
                  data-testid="login-submit-button"
                >
                  {t('auth:login.submit')}
                </FormButton>
              </form>
            )}

            {/* Signup Section */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Text
                variant="bodySmall"
                className="text-center text-slate-600 dark:text-slate-400 mb-3"
              >
                {t('auth:login.noAccount')}
              </Text>
              <FormButton
                type="button"
                variant="primary"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (inviteToken) params.set('invite', inviteToken);
                  if (langParam) params.set('lang', langParam);
                  const query = params.toString();
                  router.push(query ? `/register?${query}` : '/register');
                }}
                fullWidth
                data-testid="login-register-link"
                className="!ring-emerald-500 focus:!ring-emerald-500"
                style={{
                  backgroundImage: 'none',
                  backgroundColor: '#10b981',
                }}
              >
                {t('auth:login.signUpLink')}
              </FormButton>
            </div>
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
